import React, { createContext, useContext, useEffect, useState } from "react";
import { useInventoryContext } from "./InventoryContext";
import { usePrinterContext } from "./PrinterContext";
import { API } from "@/lib/api";
import { useLocation, useSearch } from "wouter";
import { useErrorModalContext } from "./ErrorModalContext";
import { resyncAllSalesData, resyncSellerData } from "@/api/sync";
import { getTeams } from "@/api/teams";
import { AssistantResponse, AssistantStatus, PaymentStatus, SettingsReponse, ShippingTemplate, SyncJobStatus, UserRole, UserStatus } from "@boxem/core-api-schema";
import { endSession, getMe, startSession } from "@/api/auth";
import { useSubscriptionContext } from "./SubscriptionContext";
import {useBuyCostContext} from "@/contexts/BuyCostContext";
import { LocalStorageKeys } from "@/utils/localStorageUtils";


export type Address = {
  id?: number;
  name: string;
  company?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  country?: string;
  zipcode: string;
  phone?: string;
  email?: string;
};

export enum UserGroup {
  Admin = "admin",
  User = "user",
}

export type Seller = {
  uuid: string;
  name: string;
  email: string;
  role: string;
  sellerId?: string;
  recurlyAccountCode: string;
  paymentStatus: PaymentStatus;
}

export type User = {
  uuid: string;
  name: string;
  email: string;
  emailVerified?: boolean;
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
  status: UserStatus;
  role: UserRole;
  sellerId?: string; // if null, seller account is not connected.
  isActive: boolean;
  activeMarketplaceId?: string;
  isSubscribed: boolean;
  usersToGroups: UserGroup[]; // e.g., "admin", "user", etc.
  addresses: Address[];
  shippingTemplates: ShippingTemplate[];

  listingSyncStatus?: SyncJobStatus;
  shipmentSyncStatus?: SyncJobStatus;
  allSalesSyncStatus?: SyncJobStatus;

  recurlyAccountCode: string;
  paymentStatus: PaymentStatus;

  // preferences/settings:
  settings: SettingsReponse;
  permissions?: number;
  sellers: User[];
  goAuraIntegration: boolean;
  sellerSnapIntegration: boolean;
  bqoolIntegration: boolean;

  // WFS integration
  wfsSellerId: string;
};

const UserContext = createContext(undefined);
export interface TeamMember {
  relationId?: number;
  uuid?: string;
  email: string;
  name: string;
  role?: UserRole;
  permissions: number;
  emailVerified?: boolean;
  status?: AssistantStatus;
}

// this is a bit hacky, but there are some cases (like, in 'handlerSeller' inside lib/api.ts)
// that want to know about the current seller/user but don't want to necessarily have to live
// inside a react context and then have to deal with circular dependency issues that the react
// virtual DOM has to think about.
export const globalUser: { user: User; seller: User; } = { user: null, seller: null };

export const useUserContext = () => useContext<{

  // the seller is the user representing the user that originally signed-up and connected to seller-central
  seller: User | undefined; 

  // this is the logged in user (could be admin/assistant/support/)
  user: User | undefined;  

  // a merged list of the seller's addresses + the logged-in user's addresses. you should use this instead of seller/user.addresses
  addressBook: Address[]; 
  pushNewAddress: (address: Address) => void;
  updateAddress: (address: Address) => void;

  // whether or not a call to `getMe` has completed or not.
  // this tells you whether or not you know for sure if the user is logged-in
  // (because you can't know purely based on the presence of the `refresh_token` cookie,
  // you have to go to the API to ask if the refresh_token is still valid).
  hasTriedToRefreshSession: boolean;

  // pass 'refetchBatchesAndInventory' if you want to initiate a reload of all the seller's 
  // inventory and shipment data. note it does not refetch sales data.
  setUser: (user: Partial<User> | undefined, refetchBatchesAndInventory: boolean) => void;

  // this is used in a few specific scenarios, the vast majority of the time you want 'setUser' instead.
  // 
  // 1. when you are an assistant account and have multiple sellers, and are switching between them. 
  //    in all other situations, the seller is set implicitly by setting the user - most of the time, 
  //    the seller === the user. less of the time, but still more common is that the seller is user.sellers[0]
  //    (the single seller associated with an assistant account). This is enabled by a <Select/> on
  //    the dashboard.
  //
  // 2. when updating data that is relevant to the seller, not the user. this generally happens when
  //    updating settings (like variable SKU generation settings), or doing the initial sync process.
  //
  setSeller: (seller: User | undefined, refetchBatchesAndInventory?: boolean) => void;

  teamLoading: boolean;
  fetchTeam: () => void;
  team: TeamMember[] | undefined;
  setTeam: (team: TeamMember[]) => void;

  logout: (redirectToLoginPage?: boolean) => void;

  resyncSellerData: () => Promise<void>;
  resyncAllSalesData: () => Promise<void>;

}>(UserContext);

export const UserContextProvider = (props: { children: React.ReactNode }) => {
  const searchParams = new URLSearchParams(useSearch());

  const { refetchWholeInventory, clearInventory } = useInventoryContext();
  const { fetchCategories } = useBuyCostContext()
  const { initiateConnectionToQzTray } = usePrinterContext();
  const { handleErrorResponseGenerically } = useErrorModalContext();
  const { refetchSubscription } = useSubscriptionContext();
  const [_, navigate] = useLocation();

  const [hasTriedToRefreshSession, setHasTriedToRefreshSession] = useState<boolean>(false);

  const [user, setUser] = useState<User | undefined>();
  const [seller, setSeller] = useState<User | undefined>();
  const [addressBook, setAddressBook] = useState<Address[]>([]);

  const [team, setTeam] = useState<TeamMember[]>([]);
  const [teamLoading, setTeamLoading] = useState<boolean>(false);

  const logout = (redirectToLoginPage: boolean = true) => {
    clearInventory();
    endSession();
    API.logout();
    if (redirectToLoginPage) navigate("~/auth/login");
  };

  const reFetchTeam = (ownerUUID: string) => {
    if (!!ownerUUID) {
      setTeamLoading(true);
      getTeams(ownerUUID)
        .then((data: AssistantResponse) => {
          if (data?.assistants.length > 0) {
            setTeam(data.assistants)
          }
        })
        .finally(() => setTeamLoading(false));
    }
  };

  const refetchUserData = () => {
    refetchSubscription();
    refetchWholeInventory();
    fetchCategories();
  };

  const pushNewAddress = (address: Address) => {
    user.addresses.push(address);
    setUser({...user});
  };

  const updateAddress = (address: Address) => {
    const index = user.addresses.findIndex(a => a.id === address.id);
    if (index !== -1) {
      user.addresses[index] = address;
      setUser({...user});
    }
  };

  const handleUserInitOrUpdate = (user: User, shouldRefetchUserData: boolean, connectToQzTray: boolean) => {
    if (!user) {
      // user was set to null, just clear everything
      clearInventory();
      return;
    }
    
    globalUser.user = user;
    if (user) {
      let currSeller: User = user;
      if (user.role !== UserRole.SELLER && user.role !== UserRole.ADMIN && user.role !== UserRole.SUPPORT) {
        currSeller = user.sellers[0];
      }

      setSeller(currSeller);
      globalUser.seller = currSeller;
      startSession();

      if (user.status === UserStatus.NORMAL && user.role !== UserRole.ADMIN && user.role !== UserRole.SUPPORT) {
        reFetchTeam(currSeller.uuid);

        if (shouldRefetchUserData) {
          refetchUserData();
        }
      }

      // attempt to connect to Boxem Print
      if (connectToQzTray) initiateConnectionToQzTray();
    }
  };

  const handleSession = () => {
    if (!!user) {
      if (document.hidden) {
        endSession();
      } else {
        startSession();
      }
    }
  };

  useEffect(() => {
    //This is here to prevent a race condition from reverting the user state after we've successfully mirrored a users account.
    const mirrorToken = searchParams.get("mt"); // "mt" -> Mirror Token
    if (!mirrorToken) {  
      getMe()
        .then((user: any /*UserResponse*/) => { // @TODO fix the 'any' here...
          setUser(user);
          handleUserInitOrUpdate(user, true, true);
          document.addEventListener("visibilitychange", handleSession);
        })
        .catch(e => {
          logout(false); // do this to clear the refresh token cookie (we can't do it client-side because it's an HTTP only cookie)
        })
        .finally(() => {
          setHasTriedToRefreshSession(true);
        });
    }

    // @TODO(nick) remove both of these calls in 1 month
    window.localStorage.removeItem(LocalStorageKeys.user);
    window.localStorage.removeItem(LocalStorageKeys.seller);

    //If context is unmounted (tab closed or inactive) we will end the user session.
    return () => {
      document.removeEventListener("visibilitychange", handleSession);
    };
  }, []);

  useEffect(() => {
    const cmp = (a: Address, b: Address) => a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    if (user?.sellerId !== seller?.sellerId) {
      setAddressBook([...(user?.addresses) ?? [], ...(seller?.addresses ?? [])].sort(cmp));
    } else {
      // if the user and the seller are the same, they will both contain the same addresses.
      // avoid ending up with 2x the total count of addresses we should have:
      setAddressBook([...(user?.addresses) ?? []].sort(cmp));
    }
  }, [user, seller]);

  return (
    <UserContext.Provider value={{
      user,
      setUser: (user: User, shouldRefetchUserData: boolean) => {
        setUser({...user});
        handleUserInitOrUpdate(user, shouldRefetchUserData, false);
        globalUser.user = user;
      },
      seller,
      setSeller: (seller: User, shouldRefetchUserData: boolean = false) => {
        setSeller({...seller});
        if (shouldRefetchUserData && !!seller.sellerId) {
          refetchUserData();
        }
        globalUser.seller = seller;
      },
      addressBook,
      hasTriedToRefreshSession,
      pushNewAddress,
      updateAddress,
      logout,
      resyncSellerData: () => {
        return resyncSellerData()
          .then(result => {
            setSeller({ ...seller, ...result });
          })
          .catch(errors => {
            if (handleErrorResponseGenerically(errors)) {
              return;
            }
          });
      },
      resyncAllSalesData: () => {
        return resyncAllSalesData()
          .then(result => {
            setSeller({ ...seller, ...result });
          })
          .catch(errors => {
            if (handleErrorResponseGenerically(errors)) {
              return;
            }
          });
      },
      team,
      setTeam: (team: TeamMember[]) => {
        setTeam(team);
      },
      teamLoading,
    }}>
      {props.children}
    </UserContext.Provider>
  );
};
